/**
 * @desc        Auth Base
 * @version     1.0.1 (Added fade animation to the background to make it a bit nicer :) ) - 01 September 2026
 */

import Fade from '@mui/material/Fade'
import React, { useEffect, useState } from 'react'
import MediaQuery from 'react-responsive'
import { Col, Row } from 'reactstrap'
import BackgroundCarousel from './carousel-background'

import './auth-base.scss'

/** The basic layout for the authentication and error pages.
 *
 * @param {string} [props.background=field] - One of "field", "cow", "sheep", "carousel".
 * @param {string} [props.mobileBackground] - One of "field", "cow", "sheep".
 * @param {Component} [props.bottom] - Contents to be displayed at the bottom of the container. */

function AuthBase({ children, ...props }) {
    const renderBackground = () => {
        switch (props.background) {
            case 'sheep':
                return <div className="bg-sheep posn-abs-full" />
            case 'cow':
                return <div className="bg-cow posn-abs-full" />
            case 'carousel':
                return (
                    <div className="bg-carousel">
                        <BackgroundCarousel></BackgroundCarousel>
                    </div>
                )
            default:
                return <div className="bg-field posn-abs-full" />
        }
    }

    const renderMobileBackground = () => {
        switch (props.mobileBackground) {
            case 'sheep':
                return <div className="bg-sheep" />
            case 'cow':
                return <div className="bg-cow" />
            case 'field':
                return <div className="bg-field" />
            default:
                return null
        }
    }

    return (
        <div className="auth-base">
            <MediaQuery minWidth={901}>
                <Row className="h-100 no-gutters desktop">
                    <Col xl="7" lg="6" md="6" className="vh-100 d-flex-center bg-auth-gray">
                        <Fade in={true} timeout={200}>
                            {renderBackground()}
                        </Fade>
                    </Col>
                    <Col xl="5" lg="6" md="6" className="vh-100 bg-white right">
                        <div className="content-box d-flex-dir-c d-flex-jsb" id="rh-content-box">
                            <div className="content-top-box">
                                <a href={process.env.REACT_APP_BUILD_MODE === 'web' ? 'https://www.farmdeck.com' : '/auth/login'} className="logo">
                                    <img src="/assets/img/icons/farmdeck_logo_dark.svg" alt={process.env.REACT_APP_BUILD_MODE === 'web' ? 'www.farmdeck.com' : 'Home'} title={process.env.REACT_APP_BUILD_MODE === 'web' ? 'www.farmdeck.com' : 'Home'} />
                                </a>
                            </div>

                            {/* <div className="content-center-box"></div> */}
                            {children}

                            <div className="content-bottom-box">{props.bottom}</div>
                        </div>
                    </Col>
                </Row>
            </MediaQuery>
            <MediaQuery maxWidth={900}>
                <div className="no-gutters mobile">
                    <div className={'content-box' + (props.mobileBackground ? ' with-background' : '')}>
                        <div className="content-top-box">
                            <a href={process.env.REACT_APP_BUILD_MODE === 'web' ? 'https://www.farmdeck.com' : '/auth/login'} className="logo">
                                <img src="/assets/img/icons/farmdeck_logo_dark.svg" alt={process.env.REACT_APP_BUILD_MODE === 'web' ? 'www.farmdeck.com' : 'Home'} title={process.env.REACT_APP_BUILD_MODE === 'web' ? 'www.farmdeck.com' : 'Home'} />
                            </a>
                            {props.mobileBackground ? (
                                <Fade in timeout={200}>
                                    {renderMobileBackground()}
                                </Fade>
                            ) : null}
                        </div>

                        <div className="content-center-box">{children}</div>

                        <div className="content-bottom-box">{props.bottom}</div>
                    </div>
                </div>
            </MediaQuery>
        </div>
    )
}

export const useErrorMessage = () => {
    const [msg, setMsg] = useState('')
    const [classes, setClasses] = useState(' error-message hidden')

    useEffect(() => {
        if (classes === ' error-message') {
            const timeid = setTimeout(() => setClasses(' error-message hidden'), 10000)
            return () => clearTimeout(timeid)
        }
    }, [classes])

    const setErrorMessage = message => {
        if (message) {
            setMsg(message)
            setClasses(' error-message')
        } else {
            setClasses(' error-message hidden')
        }
    }

    return [msg, classes, setErrorMessage]
}

export default AuthBase
